import { useCallback, useEffect, useRef, useState } from "react";
import { HumanSupportBanner } from "@/components/recruiter/HumanSupport";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { CircleCheck, Search as SearchIcon, SearchX } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { NativeSelect, PageHeader, Spinner, Empty, TierBadge } from "@/components/Common";
import { GettingStarted } from "@/components/tour/GettingStarted";
import { markStep } from "@/lib/onboarding";
import { JobCard } from "@/components/JobCard";
import { JobDetailSheet } from "@/components/JobDetailSheet";
import { ReadyFilter, useReadyToggle } from "@/components/ReadyFilter";
import { isReady, useAtsTick } from "@/lib/atsScores";
import { useAuth } from "@/context/AuthContext";
import { api, enc, errMsg, isUpgrade, tierRank } from "@/lib/api";
import { cn } from "@/lib/utils";
import { defaultSearch, stateName, targetStates, withLocation } from "@/lib/profileSearch";

const BOARD_DATES = ["all", "today", "3days", "week", "month"];
const ATS_DATES = ["all", "week", "month"];
const BOARDS = ["all", "ashby", "greenhouse", "lever", "smartrecruiters", "workday", "bamboohr", "jobvite", "icims", "jazzhr", "workable"];
const TYPES = [{ value: "", label: "Any type" }, { value: "FULLTIME", label: "Full-time" }, { value: "PARTTIME", label: "Part-time" }, { value: "CONTRACTOR", label: "Contract" }, { value: "INTERN", label: "Internship" }];
const DATE_LABEL = { all: "Any time", today: "Today", "3days": "Last 3 days", week: "Past week", month: "Past month" };

export default function Search() {
  const { profile, tier } = useAuth();
  const location = useLocation();
  const preset = location.state?.alert;
  const [p, setP] = useState(() => {
    const d = defaultSearch(profile);
    return {
      source: preset?.source || "boards", query: preset?.query || d.query, location: preset ? "" : d.location,
      remote: preset ? !!preset.remote : d.remote, employment_type: preset?.employment_type || "", date_posted: preset?.date_posted || "all", board: preset?.board || "all",
    };
  });
  const [newIds] = useState(() => new Set(preset?.new_ids || []));
  const [jobs, setJobs] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [upgrade, setUpgrade] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [ready, setReady] = useReadyToggle("search");
  const reqId = useRef(0);
  const sentinel = useRef(null);

  const fetchPage = useCallback(async (params, cur) => {
    const id = ++reqId.current;
    setLoading(true);
    const q = { query: params.source === "boards" ? withLocation(params.query, params.location) : params.query, remote: params.remote, date_posted: params.date_posted };
    if (params.source === "ats") q.board = params.board;
    else if (params.employment_type) q.employment_type = params.employment_type;
    if (cur) q.cursor = cur;
    try {
      const { data } = await api.get(params.source === "ats" ? "/jobs/ats-search" : "/jobs/search", { params: q });
      if (id !== reqId.current) return;
      setJobs((j) => (cur ? [...j, ...(data.jobs || [])] : data.jobs || []));
      setCursor(data.cursor || null);
      setUpgrade(false);
    } catch (e) {
      if (id !== reqId.current) return;
      if (isUpgrade(e)) setUpgrade(true);
      else toast.error(errMsg(e));
      setCursor(null);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, []);

  const submitted = useRef(p);
  const run = (next = p) => { submitted.current = next; setJobs([]); setCursor(null); fetchPage(next, null); };
  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const ob = new IntersectionObserver(([e]) => { if (e.isIntersecting && cursor && !loading) fetchPage(submitted.current, cursor); }, { rootMargin: "400px" });
    ob.observe(el);
    return () => ob.disconnect();
  }, [cursor, loading, fetchPage]);

  const setSource = (source) => {
    if (source === "ats" && tierRank(tier) < 2) { window.dispatchEvent(new CustomEvent("rpopro:upgrade", { detail: { message: "ATS direct search is an Elite feature", tier: "elite" } })); return; }
    const next = { ...p, source, date_posted: source === "ats" && !ATS_DATES.includes(p.date_posted) ? "all" : p.date_posted };
    setP(next); run(next);
  };

  const toggleSave = async (job) => {
    try {
      if (job.status) await api.delete(`/applications/${enc(job.id)}`);
      else await api.post("/applications", { job_id: job.id });
      setJobs((js) => js.map((j) => (j.id === job.id ? { ...j, status: job.status ? null : "saved" } : j)));
      toast.success(job.status ? "Removed" : "Saved to tracker");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const refreshOne = async () => {
    try {
      const { data } = await api.get(`/jobs/${enc(openId)}`);
      setJobs((js) => js.map((j) => (j.id === openId ? { ...j, status: data.application?.status || null } : j)));
    } catch {}
  };

  const dates = p.source === "ats" ? ATS_DATES : BOARD_DATES;
  const passMin = useAtsTick();
  const shown = ready ? jobs.filter((j) => isReady(j.id, passMin)) : jobs;
  const roles = profile?.target_roles || [];
  const states = targetStates(profile);
  return (
    <div>
      <PageHeader title="Job Search" subtitle="Search millions of listings across job boards, or go straight to company ATS systems." />
      <HumanSupportBanner where="dashboard" />
      <GettingStarted />
      <div className="mb-6 inline-flex rounded-full border border-[#27272A] bg-[#18181B] p-1" data-testid="source-toggle">
        {[["boards", "Job boards"], ["ats", "ATS direct"]].map(([v, l]) => (
          <button key={v} onClick={() => setSource(v)} data-testid={`source-${v}`}
            className={cn("flex items-center gap-2 rounded-full px-4 py-1.5 text-sm transition-colors", p.source === v ? "bg-[#D4AF37] text-black" : "text-zinc-400 hover:text-zinc-100")}>
            {l}{v === "ats" && <TierBadge tier="elite" className={p.source === v ? "border-black/30 bg-black/10 text-black" : ""} />}
          </button>
        ))}
      </div>
      {roles.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="target-role-chips">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">Your targets</span>
          {roles.map((r) => (
            <button key={r} type="button" onClick={() => { const next = { ...p, query: r }; setP(next); run(next); }} data-testid={`target-role-${r}`}
              className={cn("rounded-full border px-3 py-1 text-xs transition-colors", p.query === r ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F3C649]" : "border-[#3F3F46] text-zinc-400 hover:border-[#D4AF37]/60 hover:text-zinc-200")}>{r}</button>
          ))}
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); markStep("search"); run(); }} className="mb-8 flex flex-col gap-3 rounded-xl border border-[#27272A] bg-[#18181B] p-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input value={p.query} onChange={(e) => setP({ ...p, query: e.target.value })} placeholder="Job title, skill or company" className="h-10 border-transparent bg-[#09090B] pl-9" data-testid="search-query-input" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {p.source === "boards" ? (
            <>
              <NativeSelect value={p.employment_type} onChange={(v) => setP({ ...p, employment_type: v })} options={TYPES} data-testid="employment-type-select" />
              {states.length > 0 && <NativeSelect value={p.location} onChange={(v) => setP({ ...p, location: v })} options={[{ value: "", label: "Any location" }, ...states.map((s) => ({ value: s, label: stateName(s) }))]} data-testid="location-select" />}
            </>
          ) : (
            <NativeSelect value={p.board} onChange={(v) => setP({ ...p, board: v })} options={BOARDS.map((b) => ({ value: b, label: b === "all" ? "All ATS boards" : b }))} className="capitalize" data-testid="board-select" />
          )}
          <NativeSelect value={p.date_posted} onChange={(v) => setP({ ...p, date_posted: v })} options={dates.map((d) => ({ value: d, label: DATE_LABEL[d] }))} data-testid="date-posted-select" />
          <label className="flex h-10 items-center gap-2 rounded-md border border-[#3F3F46] px-3 text-sm text-zinc-300">
            <Switch checked={p.remote} onCheckedChange={(v) => setP({ ...p, remote: v })} data-testid="remote-switch" />Remote
          </label>
          <Button type="submit" className="h-10 px-6" data-testid="search-submit-button">Search</Button>
        </div>
      </form>

      {upgrade ? (
        <Empty icon={SearchX} title="ATS direct is an Elite feature" text="Upgrade to search Greenhouse, Lever, Ashby, Workday and 6 more ATS boards directly." />
      ) : (
        <>
          <div className="mb-4"><ReadyFilter on={ready} onChange={setReady} min={passMin} count={shown.length} /></div>
          <div className="grid gap-4 xl:grid-cols-2" data-testid="job-results">
            {shown.map((j, i) => <JobCard key={`${j.id}-${i}`} job={j} index={i} isNew={newIds.has(j.id)} onOpen={(job) => setOpenId(job.id)} onToggleSave={toggleSave} findPeople />)}
          </div>
          {ready && jobs.length > 0 && shown.length === 0 && <Empty icon={CircleCheck} title="No ready jobs yet" text={`None of these results has an ATS score of ${passMin}% or more. Run an ATS check on a job to see if it's ready.`} />}
        </>
      )}
      {!loading && !upgrade && jobs.length === 0 && <Empty icon={SearchX} title="No jobs found" text="Try a broader title or remove some filters." />}
      <div ref={sentinel} className="flex h-20 items-center justify-center">{loading && <Spinner className="h-6 w-6 text-[#D4AF37]" />}</div>
      <JobDetailSheet jobId={openId} onClose={() => setOpenId(null)} onChanged={refreshOne} />
    </div>
  );
}
